// ─────────────────────────────────────────────────────────────────────────────
// O texto do documento, em blocos separados pelas imagens.
//
// POR QUE ISTO EXISTE. Antes, o texto inteiro ia para o agente com as imagens
// viradas em `[IMAGEM 1]`, `[IMAGEM 2]`, e o prompt pedia que ele preservasse
// esses marcadores. Agente de linguagem não é obrigado a obedecer: ele comia os
// marcadores ao reescrever o parágrafo, e o código, sem ter onde recolocar as
// figuras, empilhava todas no FIM do documento. Era exatamente o que se queria
// evitar.
//
// Agora o agente nunca vê imagem nenhuma. O texto é PICADO nos pontos onde as
// imagens estão, e só os pedaços de texto viajam. A ordem não depende de
// obediência: ela está na estrutura, e é remontada aqui.
//
//   "abriu a tela  [img A]  clicou em salvar  [img B]  deu erro"
//        bloco 0              bloco 1                    bloco 2
//
// O agente devolve os três blocos reescritos; as imagens voltam entre eles, nas
// mesmas posições. Um bloco que ele esqueça volta ao texto ORIGINAL — pior é
// perder o que o operador escreveu.
//
// Este arquivo é PURO: não conhece webhook, React nem Electron. Quem fala com o
// n8n é conclusaoDocumento.js. A separação é o que permite testar a regra sem
// subir a aplicação (scripts/teste-conclusao-documento.mjs).
//
// Nada disso vale para a interação do ticket: no MySuite o texto continua
// exatamente como sempre foi. Este arquivo só serve ao documento.
// ─────────────────────────────────────────────────────────────────────────────

import { partesDoTexto, marcadorDe } from './textoComAnexos.js';

/**
 * Divide o texto em peças, na ordem: texto, imagem, texto, imagem…
 *
 * Devolve `{ pecas, blocos }`, onde `pecas` é a estrutura completa (o esqueleto
 * do documento) e `blocos` são apenas os textos, na ordem — o que vai para o
 * agente. O índice de cada bloco é o índice dele em `blocos`, e é por ele que a
 * remontagem devolve cada pedaço ao seu lugar.
 *
 * Blocos vazios (duas imagens seguidas, imagem no começo) NÃO viram bloco: não
 * há o que reescrever, e mandar vazio só confunde o agente.
 */
export function dividirEmBlocos(texto) {
  const pecas = [];
  const blocos = [];

  partesDoTexto(texto).forEach((parte) => {
    if (parte.tipo === 'anexo') { pecas.push({ tipo: 'imagem', id: parte.id }); return; }

    const valor = String(parte.valor || '').trim();
    if (!valor) return;
    pecas.push({ tipo: 'texto', indice: blocos.length, original: valor });
    blocos.push(valor);
  });

  return { pecas, blocos };
}

/**
 * Remonta o documento com os blocos que o agente devolveu.
 *
 * A ordem das peças é a do operador, sempre. `reescritos[i]` só substitui o
 * bloco `i` quando vem preenchido: resposta curta demais, item vazio ou fluxo
 * fora do ar deixam o texto original no lugar.
 */
export function remontarBlocos(pecas, reescritos = []) {
  const partes = pecas.map((peca) => {
    if (peca.tipo === 'imagem') return marcadorDe(peca.id);
    const novo = String(reescritos[peca.indice] ?? '').trim();
    return novo || peca.original;
  });

  return partes.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Quantos blocos o agente devolveu de forma aproveitável.
 *
 * Serve para a tela dizer se o texto foi mesmo formalizado ou se saiu como o
 * operador escreveu — um "deu certo" que não é verdade custa a confiança no
 * resto do documento.
 */
export function quantosAproveitados(blocos = [], reescritos = []) {
  return blocos.reduce(
    (total, _, i) => total + (String(reescritos[i] ?? '').trim() ? 1 : 0),
    0,
  );
}

// ── Triagem do que o agente devolve ──────────────────────────────────────────
//
// POR QUE ISTO EXISTE. Um nó mal configurado no n8n devolve o PRÓPRIO PROMPT do
// agente no lugar da resposta — "Você reescreve relatos de atendimento…", as
// regras numeradas e tudo o mais. Para o código isso é texto como qualquer
// outro: entrava no lugar do bloco e ia parar no PDF que o cliente recebe.
//
// Não dá para confiar em o fluxo estar sempre certo. Então o texto que volta é
// TRIADO aqui antes de entrar no documento, e o que não passa é descartado — o
// bloco original do operador fica no lugar, que é sempre melhor do que instrução
// de agente impressa no papel.

/** Frases que só aparecem em instrução de agente, nunca num relato reescrito. */
const PISTAS_FORTES = [
  /voc[êe]\s+reescreve\s+relatos/i,
  /devolva\s+(somente|apenas)\s+um\s+json/i,
  /regras?\s+dos?\s+blocos/i,
  /regra\s+do\s+t[íi]tulo/i,
  /mesma\s+quantidade\s+de\s+blocos/i,
  /"blocos"\s*:/i,
  /"titulo"\s*:/i,
  /\bsitua[çc][ãa]o\s+reportada\b\s*"/i,
  /n[ãa]o\s+invente\s+nada/i,
  /quantidade_blocos/i,
];

/** Pistas que sozinhas não condenam, mas juntas denunciam o prompt. */
const PISTAS_FRACAS = [
  /portugu[êe]s\s+do\s+brasil/i,
  /terceira\s+pessoa/i,
  /sem\s+sauda[çc][ãa]o/i,
  /jarg[ãa]o\s+interno/i,
  /nunca\s+reordene/i,
  /n[ãa]o\s+use\s+listas/i,
  /markdown/i,
  /bloco\s+de\s+c[óo]digo/i,
  /n[úu]mero\s+de\s+ticket/i,
  /texto\s+corrido/i,
  /legendas?\b.*\bordem\b/i,
];

/**
 * O texto devolvido é instrução de agente, e não relato reescrito?
 *
 * Uma pista forte basta. Duas fracas juntas também — o prompt inteiro tem
 * dezenas delas, enquanto um relato de atendimento dificilmente tem duas.
 */
export function pareceInstrucao(texto) {
  const t = String(texto ?? '');
  if (!t.trim()) return false;
  if (PISTAS_FORTES.some(re => re.test(t))) return true;
  return PISTAS_FRACAS.filter(re => re.test(t)).length >= 2;
}

// Um bloco reescrito fica perto do tamanho do original (é regra do prompt). Se
// vier MUITO maior, ou o agente inventou meia página, ou colou algo que não é o
// relato. O piso em caracteres evita punir o bloco de uma linha que virou duas.
const FATOR_MAXIMO = 3;
const FOLGA_MINIMA = 320;

/** O bloco cresceu além do que uma reescrita explica? */
export function cresceuDemais(original, reescrito) {
  const a = String(original ?? '').trim().length;
  const b = String(reescrito ?? '').trim().length;
  if (!a || !b) return false;
  return b > Math.max(a * FATOR_MAXIMO, a + FOLGA_MINIMA);
}

/**
 * Passa os blocos reescritos pela triagem, na posição de cada um.
 *
 * O que não passa vira string vazia — e string vazia, em `remontarBlocos`, é
 * exatamente o caminho que devolve o texto ORIGINAL daquele trecho. Ou seja: o
 * documento nunca fica com buraco, no máximo fica sem polimento naquele pedaço.
 *
 * Devolve `{ blocos, descartados }`; `descartados` é o que a tela usa para
 * avisar que o documento saiu pela metade.
 */
export function triarBlocos(originais = [], reescritos = []) {
  let descartados = 0;

  const blocos = originais.map((original, i) => {
    const novo = String(reescritos[i] ?? '').trim();
    if (!novo) return '';
    if (pareceInstrucao(novo) || cresceuDemais(original, novo)) {
      descartados += 1;
      return '';
    }
    return novo;
  });

  return { blocos, descartados };
}

// Título é uma frase curta (a regra do prompt diz até 8 palavras). Qualquer
// coisa muito acima disso é sinal de que veio outra coisa no lugar.
const TITULO_MAX_CARACTERES = 120;

/** O título do documento, ou string vazia quando não dá para confiar nele. */
export function triarTitulo(titulo) {
  const t = String(titulo ?? '').trim();
  if (!t) return '';
  if (t.length > TITULO_MAX_CARACTERES) return '';
  if (pareceInstrucao(t)) return '';
  // JSON que escapou do parser, ou o agente respondendo em bloco de código.
  if (/^[[{]/.test(t) || /```/.test(t)) return '';
  return t;
}

/** Limpa o que os agentes costumam grudar no texto. */
function limpar(valor) {
  return String(valor ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    // O agente às vezes devolve o texto embrulhado num bloco de código.
    .replace(/^```[a-z]*\n?/i, '')
    .replace(/```$/, '')
    .trim();
}

/**
 * Lê a resposta do fluxo. Os fluxos do n8n não respondem todos igual: pode vir
 * objeto, array de um item, ou o JSON dentro de uma string.
 *
 * Devolve `{ titulo, blocos }` — `blocos` sempre array (vazio quando não veio).
 */
export function lerResposta(dados) {
  let r = Array.isArray(dados) ? dados[0] : dados;

  if (typeof r === 'string') {
    try { r = JSON.parse(r); } catch (e) { return { titulo: '', blocos: [] }; }
    if (Array.isArray(r)) [r] = r;
  }
  if (r == null || typeof r !== 'object') return { titulo: '', blocos: [] };

  // `output`/`data` são os embrulhos mais comuns dos nós de agente.
  const corpo = (r.blocos || r.titulo) ? r : (r.output || r.data || r.resposta || r);
  const lista = corpo?.blocos || corpo?.textos || [];

  return {
    titulo: limpar(corpo?.titulo || corpo?.title || ''),
    blocos: Array.isArray(lista) ? lista.map(limpar) : [],
  };
}
